import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCapital } from "@/context/CapitalContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Share2, Copy, Trash2, Check, Link as LinkIcon, Loader2, ExternalLink, FileDown } from "lucide-react";
import { toast } from "sonner";
import { TID } from "@/constants/testIds";
import { API_BASE } from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}

export function ShareLinkDialog({ open, onOpenChange }) {
  const { client } = useCapital();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [expiry, setExpiry] = useState(30);
  const [copiedToken, setCopiedToken] = useState(null);

  const load = async () => {
    if (!client) return;
    setLoading(true);
    try {
      const { data } = await api.get("/capital/share-links", { params: { client } });
      setLinks(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open, client]);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/capital/share-links", {
        client,
        expires_in_days: Number(expiry) || 30,
        label: label.trim() || null,
      });
      setLabel("");
      toast.success("Share link created");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create link");
    } finally { setCreating(false); }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this share link? The client will lose access immediately.")) return;
    try {
      await api.post(`/capital/share-links/${token}/revoke`);
      toast.success("Share link revoked");
      await load();
    } catch { toast.error("Could not revoke link"); }
  };

  const deleteLink = async (token) => {
    if (!window.confirm("Permanently delete this share link?")) return;
    try {
      await api.delete(`/capital/share-links/${token}`);
      toast.success("Deleted");
      await load();
    } catch { toast.error("Could not delete"); }
  };

  const copy = async (token) => {
    const url = `${window.location.origin}/client-view/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopiedToken(null), 1600);
    } catch {
      toast.error("Copy failed — copy manually: " + url);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Share2 className="w-5 h-5 text-gbe" />
            Share with client — {client}
          </DialogTitle>
          <DialogDescription>
            Generate a read-only link that opens the Board Report directly. Send it to your client — no login required.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium mb-2">Create a new link</div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_auto] gap-3">
            <div>
              <Label htmlFor="share-label" className="text-xs text-slate-600">Label (optional)</Label>
              <Input
                id="share-label"
                data-testid={TID.capitalShareLabel}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Q1 Board Pack"
                className="mt-1 h-9 bg-white"
              />
            </div>
            <div>
              <Label htmlFor="share-expiry" className="text-xs text-slate-600">Expires in (days)</Label>
              <Input
                id="share-expiry"
                data-testid={TID.capitalShareExpiry}
                type="number"
                min={1}
                max={365}
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
                className="mt-1 h-9 bg-white"
              />
            </div>
            <div className="flex items-end">
              <Button
                data-testid={TID.capitalShareCreate}
                onClick={create}
                disabled={creating}
                className="bg-gbe hover:bg-gbe-hover text-white h-9"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LinkIcon className="w-4 h-4 mr-1.5" />Create link</>}
              </Button>
            </div>
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium mb-2">
            Active & past links {loading && <Loader2 className="inline w-3 h-3 animate-spin ml-1" />}
          </div>
          {links.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-md">
              No links yet. Create one above.
            </div>
          ) : (
            <div className="max-h-[320px] overflow-y-auto space-y-2">
              {links.map((l) => {
                const url = `${window.location.origin}/client-view/${l.token}`;
                const active = l.is_active;
                return (
                  <div key={l.token} className={`border rounded-md p-3 ${active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50/60 opacity-70"}`}>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-medium ${
                            l.revoked ? "bg-slate-100 text-slate-500 border-slate-200"
                            : l.is_expired ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-emerald-50 text-emerald-700 border-emerald-200"
                          }`}>
                            {l.revoked ? "Revoked" : l.is_expired ? "Expired" : "Active"}
                          </span>
                          {l.label && <span className="text-sm font-medium text-slate-800">{l.label}</span>}
                          <span className="text-[11px] text-slate-400">viewed {l.view_count}× {l.last_viewed_at ? `· last ${formatDate(l.last_viewed_at)}` : ""}</span>
                        </div>
                        <div className="font-mono text-xs text-slate-500 truncate">{url}</div>
                        <div className="text-[11px] text-slate-400 mt-1">
                          Created {formatDate(l.created_at)} · Expires {formatDate(l.expires_at)}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {active && (
                          <>
                            <Button
                              data-testid={TID.capitalShareCopy(l.token)}
                              variant="outline"
                              size="sm"
                              onClick={() => copy(l.token)}
                              className="h-8"
                            >
                              {copiedToken === l.token ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(url, "_blank")}
                              className="h-8"
                              aria-label="Open in new tab"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              data-testid={`capital-share-pdf-${l.token}`}
                              variant="outline"
                              size="sm"
                              onClick={() => { window.location.href = `${API_BASE}/capital/public/board-report/${l.token}.pdf`; }}
                              className="h-8"
                              aria-label="Download branded PDF"
                            >
                              <FileDown className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              data-testid={TID.capitalShareRevoke(l.token)}
                              variant="outline"
                              size="sm"
                              onClick={() => revoke(l.token)}
                              className="h-8 text-rose-700 hover:bg-rose-50"
                            >
                              Revoke
                            </Button>
                          </>
                        )}
                        {!active && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteLink(l.token)}
                            className="h-8 text-slate-500"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
